/**
 * JV Deal Pricing Calculator — PDF Report Generator
 * Produces a clean, professional investor proposal document
 */
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// Shared formatting
const fmt = (v) => {
  if (v == null || isNaN(v)) return '$0';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v);
};
const fmtPct = (v, dec = 2) => {
  if (v == null || isNaN(v)) return '0%';
  return `${(v * 100).toFixed(dec)}%`;
};

// Render day range label — use "+" instead of "∞" for PDF compatibility
const dayLabel = (start, end) => {
  const endStr = (end === '' || end == null) ? '+' : end;
  return `${start}-${endStr} days`;
};

const COLORS = {
  FOREST: [26, 47, 35],
  TERRA: [198, 142, 93],
  WHITE: [255, 255, 255],
  BLACK: [0, 0, 0],
  MUTED: [107, 114, 128],
  LIGHT_BG: [249, 250, 251],
  GREEN_LIGHT: [236, 253, 245],
  GREEN_BORDER: [5, 150, 105],
};

/**
 * Generate a JV Deal Proposal PDF
 * @param {Object} params
 * @param {string[]} params.scenarios - Which scenarios to include: ['A'], ['B'], or ['A', 'B']
 */
export function generateCalculatorPDF({ deal, calcs, optionAResults, optionBResults, funding, scenarioName, scenarios = ['A', 'B'] }) {
  const doc = new jsPDF();
  const pw = doc.internal.pageSize.getWidth();
  const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  const includeA = scenarios.includes('A');
  const includeB = scenarios.includes('B');
  const includeBoth = includeA && includeB;

  // ─── HEADER ────────────────────────────────────────────
  doc.setFillColor(...COLORS.FOREST);
  doc.rect(0, 0, pw, 38, 'F');

  doc.setFillColor(...COLORS.TERRA);
  doc.rect(0, 38, pw, 1.5, 'F');

  doc.setTextColor(...COLORS.WHITE);
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text('JV DEAL PROPOSAL', 14, 16);

  if (scenarioName) {
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(scenarioName, 14, 26);
  }

  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.text(`Prepared: ${today}`, pw - 14, 14, { align: 'right' });
  doc.text('LandFlow  |  Land Finder OS', pw - 14, 22, { align: 'right' });

  doc.setTextColor(...COLORS.BLACK);

  // ─── DEAL OVERVIEW BOX ──────────────────────────────────
  let y = 48;

  doc.setFillColor(...COLORS.GREEN_LIGHT);
  doc.setDrawColor(...COLORS.GREEN_BORDER);
  doc.roundedRect(14, y, pw - 28, 22, 2, 2, 'FD');

  const boxCols = [
    { label: 'PURCHASE PRICE', value: fmt(deal.purchasePrice) },
    { label: 'SALE PRICE', value: fmt(deal.estimatedSalePrice) },
    { label: 'GROSS PROFIT', value: fmt(calcs.grossProfit) },
    { label: 'INVESTOR CAPITAL', value: fmt(calcs.totalInvestorCapital) },
  ];

  const colW = (pw - 28) / boxCols.length;
  boxCols.forEach((col, i) => {
    const x = 14 + colW * i + colW / 2;
    doc.setTextColor(...COLORS.MUTED);
    doc.setFontSize(6);
    doc.text(col.label, x, y + 8, { align: 'center' });
    doc.setTextColor(...COLORS.FOREST);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text(col.value, x, y + 17, { align: 'center' });
    doc.setFont('helvetica', 'normal');
  });

  doc.setTextColor(...COLORS.BLACK);
  y += 30;

  // ─── SECTION 1: COST BREAKDOWN (full width) ───────────
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('ACQUISITION & SALE BREAKDOWN', 14, y);
  y += 3;

  autoTable(doc, {
    startY: y,
    head: [['Cost Category', 'Amount', '', 'Sale Item', 'Amount']],
    body: [
      ['Purchase Price', fmt(deal.purchasePrice), '', 'Estimated Sale Price', fmt(deal.estimatedSalePrice)],
      ['Survey', fmt(deal.survey), '', 'Sale Closing Costs', `(${fmt(deal.saleClosingCosts)})`],
      ['Soil', fmt(deal.soil), '', `Sale Commission (${deal.saleCommission}%)`, `(${fmt(calcs.saleCommissionAmount)})`],
      ['Other Due Diligence', fmt(deal.otherDD), '', 'Gross Sales Revenue', fmt(calcs.grossSalesRevenue)],
      ['Total Due Diligence', fmt(calcs.totalDD), '', '', ''],
      ['Purchase Closing Costs', fmt(deal.purchaseClosingCosts), '', '', ''],
      ['Improvements', fmt(deal.improvements), '', '', ''],
      ['Total Acquisition Cost', fmt(calcs.totalCosts), '', 'GROSS PROFIT', fmt(calcs.grossProfit)],
    ],
    theme: 'plain',
    styles: { fontSize: 8, cellPadding: { top: 1.5, bottom: 1.5, left: 2, right: 2 } },
    headStyles: { fillColor: COLORS.FOREST, textColor: COLORS.WHITE, fontStyle: 'bold', fontSize: 7.5 },
    columnStyles: {
      0: { cellWidth: 42 },
      1: { cellWidth: 28, halign: 'right' },
      2: { cellWidth: 8 },
      3: { cellWidth: 46 },
      4: { cellWidth: 28, halign: 'right' },
    },
    didParseCell: (data) => {
      const label = data.row.raw[data.column.index];
      const boldLabels = ['Total Due Diligence', 'Total Acquisition Cost', 'Gross Sales Revenue', 'GROSS PROFIT'];
      if (boldLabels.includes(label)) {
        data.cell.styles.fontStyle = 'bold';
      }
      if (label === 'GROSS PROFIT' || label === fmt(calcs.grossProfit)) {
        data.cell.styles.fillColor = COLORS.GREEN_LIGHT;
      }
    },
    margin: { left: 14, right: 14 },
  });

  // ─── SECTION 2: FUNDING STRUCTURE (full width) ─────────
  y = doc.lastAutoTable.finalY + 8;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('FUNDING STRUCTURE', 14, y);
  y += 3;

  autoTable(doc, {
    startY: y,
    head: [['Category', 'Investor Funds', 'Land Finder Funds', 'Investor %', 'Land Finder %']],
    body: [
      ['Purchase Price', fmt(calcs.invPP), fmt(calcs.lfPP), `${100 - funding.lfPurchasePrice}%`, `${funding.lfPurchasePrice}%`],
      ['Due Diligence', fmt(calcs.invDD), fmt(calcs.lfDD), `${100 - funding.lfDDCosts}%`, `${funding.lfDDCosts}%`],
      ['Improvements', fmt(calcs.invImp), fmt(calcs.lfImp), `${100 - funding.lfImprovements}%`, `${funding.lfImprovements}%`],
      ['Closing Costs', fmt(calcs.invCC), fmt(calcs.lfCC), `${100 - funding.lfClosingCosts}%`, `${funding.lfClosingCosts}%`],
      ['TOTAL', fmt(calcs.totalInvestorCapital), fmt(calcs.totalLFInvestment), '', ''],
    ],
    theme: 'grid',
    styles: { fontSize: 8, cellPadding: { top: 1.5, bottom: 1.5, left: 2, right: 2 } },
    headStyles: { fillColor: COLORS.FOREST, textColor: COLORS.WHITE, fontStyle: 'bold', fontSize: 7.5 },
    columnStyles: {
      0: { cellWidth: 34 },
      1: { cellWidth: 30, halign: 'right' },
      2: { cellWidth: 30, halign: 'right' },
      3: { cellWidth: 24, halign: 'center' },
      4: { cellWidth: 24, halign: 'center' },
    },
    didParseCell: (data) => {
      if (data.row.raw[0] === 'TOTAL') {
        data.cell.styles.fontStyle = 'bold';
        data.cell.styles.fillColor = COLORS.LIGHT_BG;
      }
    },
    alternateRowStyles: { fillColor: COLORS.LIGHT_BG },
    margin: { left: 14, right: 14 },
  });

  // ─── SECTION 3: INVESTOR RETURNS — OPTION A ────────────
  y = doc.lastAutoTable.finalY + 8;

  if (includeA) {
    if (y > 235) { doc.addPage(); y = 20; }

    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.FOREST);
    doc.text(includeBoth ? 'INVESTOR RETURNS — OPTION A' : 'INVESTOR RETURNS', 14, y);
    doc.setTextColor(...COLORS.BLACK);
    y += 3;

    autoTable(doc, {
      startY: y,
      head: [['Holding Period', 'Inv. Split', 'Investor Profit', 'CoC', 'Ann. ROI', 'LF Split', 'LF Profit']],
      body: optionAResults.map(r => [
        dayLabel(r.startDay, r.endDay),
        `${r.investorSplit}%`,
        fmt(r.investorAmount),
        fmtPct(r.investorROI),
        r.annualizedROI != null ? fmtPct(r.annualizedROI) : '--',
        `${r.lfSplit}%`,
        fmt(r.lfAmount),
      ]),
      theme: 'grid',
      styles: { fontSize: 7.5, cellPadding: { top: 2, bottom: 2, left: 2, right: 2 } },
      headStyles: { fillColor: COLORS.FOREST, textColor: COLORS.WHITE, fontStyle: 'bold', fontSize: 7 },
      columnStyles: {
        0: { cellWidth: 26 },
        1: { halign: 'center', cellWidth: 18 },
        2: { halign: 'right', cellWidth: 24 },
        3: { halign: 'right', cellWidth: 18 },
        4: { halign: 'right', cellWidth: 20 },
        5: { halign: 'center', cellWidth: 18 },
        6: { halign: 'right', cellWidth: 22 },
        7: { halign: 'right', cellWidth: 22 },
      },
      alternateRowStyles: { fillColor: COLORS.LIGHT_BG },
      margin: { left: 14, right: 14 },
    });

    y = doc.lastAutoTable.finalY + 8;
  }

  // ─── SECTION 4: INVESTOR RETURNS — OPTION B ────────────
  if (includeB) {
    if (y > 235) { doc.addPage(); y = 20; }

    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.TERRA);
    doc.text(includeBoth ? 'INVESTOR RETURNS — OPTION B' : 'INVESTOR RETURNS', 14, y);
    doc.setTextColor(...COLORS.BLACK);
    y += 3;

    autoTable(doc, {
      startY: y,
      head: [['Holding Period', 'Inv. Split', 'Investor Profit', 'CoC', 'Ann. ROI', 'LF Split', 'LF Profit']],
      body: optionBResults.map(r => [
        dayLabel(r.startDay, r.endDay),
        `${r.investorSplit}%`,
        fmt(r.investorAmount),
        fmtPct(r.investorROI),
        r.annualizedROI != null ? fmtPct(r.annualizedROI) : '--',
        `${r.lfSplit}%`,
        fmt(r.lfAmount),
      ]),
      theme: 'grid',
      styles: { fontSize: 7.5, cellPadding: { top: 2, bottom: 2, left: 2, right: 2 } },
      headStyles: { fillColor: COLORS.TERRA, textColor: COLORS.WHITE, fontStyle: 'bold', fontSize: 7 },
      columnStyles: {
        0: { cellWidth: 26 },
        1: { halign: 'center', cellWidth: 18 },
        2: { halign: 'right', cellWidth: 24 },
        3: { halign: 'right', cellWidth: 18 },
        4: { halign: 'right', cellWidth: 20 },
        5: { halign: 'center', cellWidth: 18 },
        6: { halign: 'right', cellWidth: 22 },
        7: { halign: 'right', cellWidth: 22 },
      },
      alternateRowStyles: { fillColor: COLORS.LIGHT_BG },
      margin: { left: 14, right: 14 },
    });

    y = doc.lastAutoTable.finalY + 8;
  }

  // ─── SECTION 5: SIDE-BY-SIDE COMPARISON (only when both selected) ───
  if (includeBoth) {
    if (y > 225) { doc.addPage(); y = 20; }

  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('OPTION COMPARISON', 14, y);
  y += 3;

  const maxTiers = Math.max(optionAResults.length, optionBResults.length);
  const compareBody = [];
  for (let i = 0; i < maxTiers; i++) {
    const a = optionAResults[i];
    const b = optionBResults[i];
    const range = a ? dayLabel(a.startDay, a.endDay) : (b ? dayLabel(b.startDay, b.endDay) : '');
    compareBody.push([
      range.replace(' days', ''),
      a ? `${a.investorSplit}%` : '--',
      a ? fmt(a.investorAmount) : '--',
      a ? fmt(a.lfAmount) : '--',
      b ? `${b.investorSplit}%` : '--',
      b ? fmt(b.investorAmount) : '--',
      b ? fmt(b.lfAmount) : '--',
    ]);
  }

  autoTable(doc, {
    startY: y,
    head: [
      [
        { content: 'Days', colSpan: 1, styles: { halign: 'left' } },
        { content: 'OPTION A', colSpan: 3, styles: { halign: 'center' } },
        { content: 'OPTION B', colSpan: 3, styles: { halign: 'center' } },
      ],
      ['', 'Inv %', 'Inv Profit', 'LF Profit', 'Inv %', 'Inv Profit', 'LF Profit'],
    ],
    body: compareBody,
    theme: 'grid',
    styles: { fontSize: 7.5, cellPadding: { top: 2, bottom: 2, left: 2, right: 2 } },
    headStyles: { fillColor: COLORS.FOREST, textColor: COLORS.WHITE, fontStyle: 'bold', fontSize: 7, halign: 'center' },
    columnStyles: {
      0: { cellWidth: 22, halign: 'left' },
      1: { halign: 'center', cellWidth: 16 },
      2: { halign: 'right', cellWidth: 24 },
      3: { halign: 'right', cellWidth: 24 },
      4: { halign: 'center', cellWidth: 16 },
      5: { halign: 'right', cellWidth: 24 },
      6: { halign: 'right', cellWidth: 24 },
    },
    alternateRowStyles: { fillColor: COLORS.LIGHT_BG },
    margin: { left: 14, right: 14 },
  });
  }

  // ─── FOOTER on every page ──────────────────────────────
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    const ph = doc.internal.pageSize.getHeight();

    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.3);
    doc.line(14, ph - 14, pw - 14, ph - 14);

    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLORS.MUTED);
    doc.text('Confidential -- For Discussion Purposes Only', 14, ph - 8);
    doc.text(`Page ${i} of ${pageCount}`, pw - 14, ph - 8, { align: 'right' });
    doc.setTextColor(...COLORS.BLACK);
  }

  return doc;
}
